import type { EChartsInstance } from "echarts-for-react";
import React, { type CSSProperties, useEffect, useRef } from "react";
import type { ChartArea, TimeRange } from "../utils/coordinateConversion";
import { followCursorTimeLabel } from "../utils/followCursorTimeLabel";
import {
	type TimeZoneMode,
	formatDateTime,
	formatTime,
	isSameDay,
} from "../utils/timeZone";

const MS_WINDOW_LIMIT = 60 * 60 * 1000;

const LABEL_STYLE: CSSProperties = {
	position: "absolute",
	top: 0,
	left: 0,
	visibility: "hidden",
	pointerEvents: "none",
	zIndex: 5,
	padding: "5px 7px",
	borderRadius: 3,
	backgroundColor: "#536298",
	color: "#fff",
	fontSize: 12,
	fontWeight: "normal",
	fontFamily: '"Microsoft YaHei", sans-serif',
	lineHeight: 1,
	whiteSpace: "nowrap",
};

const CONNECTOR_STYLE: CSSProperties = {
	position: "absolute",
	top: 0,
	left: 0,
	width: 1,
	visibility: "hidden",
	pointerEvents: "none",
	zIndex: 4,
	backgroundColor: "rgba(83, 98, 152, 0.45)",
};

type CursorTimeLabelV2Props = {
	chart: EChartsInstance | null;
	chartArea: ChartArea | null;
	timeRange: TimeRange;
	timeZoneMode: TimeZoneMode;
};

export const CursorTimeLabelV2 = ({
	chart,
	chartArea,
	timeRange,
	timeZoneMode,
}: CursorTimeLabelV2Props) => {
	const labelRef = useRef<HTMLDivElement>(null);
	const connectorRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const label = labelRef.current;
		if (!chart || !label || !chartArea) return;

		const sameDay = isSameDay(timeRange.start, timeRange.end, timeZoneMode);
		const showMilliseconds = timeRange.end - timeRange.start <= MS_WINDOW_LIMIT;

		return followCursorTimeLabel({
			chart,
			label,
			connector: connectorRef.current,
			chartArea,
			window: timeRange,
			format: (time) =>
				sameDay
					? formatTime(time, timeZoneMode, showMilliseconds)
					: formatDateTime(time, timeZoneMode, showMilliseconds),
		});
	}, [chart, chartArea, timeRange, timeZoneMode]);

	return (
		<>
			<div ref={connectorRef} style={CONNECTOR_STYLE} />
			<div ref={labelRef} style={LABEL_STYLE} />
		</>
	);
};
